import {EditIcon, RepeatIcon, SettingsIcon} from '@chakra-ui/icons';
import {
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Link,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  OrderedList,
  Switch,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useColorModeValue,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import Card from 'components/Card/Card';
import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {League, Team} from 'types';
import {useGetRankingsMutation} from '../../../../app/api';
import {useEventContext} from '../../../../contexts/EventContext';
import {ManualRankingEditor} from './ManualRankingEditor';

interface SportEventRankingPanelProps {
  league: number;
  setSelectedLeague: (selected: number) => void;
  leagues?: League[];
  teams?: Team[];
  eventId: number;
}

export const SportEventRankingPanel: React.FC<SportEventRankingPanelProps> = ({
                                                                                league,
                                                                                eventId,
                                                                                teams,
                                                                                leagues,
                                                                                setSelectedLeague,
                                                                              }) => {
  const [rankedTeams, setRankedTeams] = useState<{ [key: number]: Team[] }>({});
  const [showRankedTeams, setShowRankedTeams] = useState<Team[]>([]);
  const {isOpen, onOpen, onClose} = useDisclosure();
  const {
    isOpen: isEditorOpen,
    onOpen: onEditorOpen,
    onClose: onEditorClose,
  } = useDisclosure();
  const [refreshRanking, setRefreshRanking] = useState(false);
  const [getRankingsMutation, {error: rankError, data}] =
    useGetRankingsMutation();
  const navigate = useNavigate();
  const buttonColor = useColorModeValue('orange', 'blue');
  const {canPerformEventAction} = useEventContext();
  const toast = useToast();

  // Effect to change the shown ranked teams (based on selected league)
  useEffect(() => {
    if (league) {
      setShowRankedTeams(rankedTeams[league] ?? []);
    }
  }, [league, rankedTeams]);

  // Effect to set ranked teams
  useEffect(() => {
    if (data && data.length && data[0].leagueId === league) {
      setRankedTeams((prev) => ({
        ...prev,
        [league]: data,
      }));
    }
  }, [data, league]);

  // Effect to display Ranking Error
  useEffect(() => {
    if (rankError) {
      toast({
        title: 'Error',
        description: (rankError as any)?.data?.message?.toString() ?? '',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [rankError, toast]);

  // Effect to set ranked teams
  useEffect(() => {
    if (leagues && teams) {
      setRankedTeams((prev) => ({
        ...prev,
        ...(leagues.reduce((acc: { [key: number]: Team[] }, league) => {
          const filteredTeams = teams.filter(
            (team) => team.leagueId === league.id
          );
          if (filteredTeams.every((team) => !!team.rank)) {
            acc[league.id] = filteredTeams.sort((a, b) => {
              return a.rank! - b.rank!;
            });
          }
          return acc;
        }, {}) ?? {}),
      }));
    }
  }, [teams, leagues]);

  const handleRefreshRankings = () => {
    if (league) {
      getRankingsMutation({
        eventId,
        leagueId: league,
        refresh: refreshRanking,
      });
    }
  };

  return (
    <Card maxW={{base: '100%', md: '300px'}} maxH="750px">
      <HStack>
        <Heading>Ranking</Heading>
        <IconButton
          icon={<RepeatIcon/>}
          variant="ghost"
          aria-label="refresh-ranking"
          onClick={handleRefreshRankings}
        />
        {canPerformEventAction('EVENT_ADMIN') && (
          <IconButton
            icon={<EditIcon/>}
            variant="ghost"
            aria-label="edit-ranking"
            onClick={onEditorOpen}
          />
        )}
        <IconButton
          variant="ghost"
          icon={<SettingsIcon/>}
          aria-label="ranking-settings"
          onClick={onOpen}
        />
        <RankingConfigurationModal
          isOpen={isOpen}
          onClose={onClose}
          shouldRefresh={refreshRanking}
          setShouldRefresh={setRefreshRanking}
        />
        <ManualRankingEditorModal
          isOpen={isEditorOpen}
          onClose={onEditorClose}
          teams={showRankedTeams}
          eventId={eventId}
          leagueId={league}
          onSave={() => {
            handleRefreshRankings();
            onEditorClose();
          }}
        />
      </HStack>
      <Tabs
        index={leagues?.findIndex((l) => l.id === league) ?? 0}
        onChange={(idx) => {
          if (leagues) {
            setSelectedLeague(leagues[idx].id);
          }
        }}
        isFitted
        colorScheme={buttonColor}
        overflowY="auto"
      >
        <TabList>
          {leagues?.map((league) => (
            <Tab key={league.id}>{league.name}</Tab>
          ))}
        </TabList>
        <TabPanels>
          {leagues?.map((league) => (
            <TabPanel key={league.id}>
              <OrderedList>
                {showRankedTeams.map((team) => (
                  <ListItem key={team.id}>
                    <HStack justify="space-between">
                      <Link
                        href={`teams/${team.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(`teams/${team.id}`);
                        }}
                      >
                        {team.name}
                      </Link>
                      <Heading size="sm">
                        {team.gamesWon}-{team.gamesLost}
                        {team.gamesTied ? '-' + team.gamesTied : ''}
                      </Heading>
                    </HStack>
                  </ListItem>
                ))}
              </OrderedList>
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </Card>
  );
};

interface RankingConfigurationModalProps {
  isOpen: boolean;
  onClose: () => void;
  shouldRefresh: boolean;
  setShouldRefresh: (refresh: boolean) => void;
}

const RankingConfigurationModal: React.FC<RankingConfigurationModalProps> = ({
                                                                               isOpen,
                                                                               onClose,
                                                                               shouldRefresh,
                                                                               setShouldRefresh,
                                                                             }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay/>
      <ModalContent>
        <ModalHeader>Ranking Configuration</ModalHeader>
        <ModalCloseButton/>
        <ModalBody>
          <FormControl>
            <FormLabel>Refresh Rankings</FormLabel>
            <Switch
              isChecked={shouldRefresh}
              onChange={(e) => setShouldRefresh(e.target.checked)}
            />
          </FormControl>
        </ModalBody>

        <ModalFooter>
          <Button colorScheme="blue" mr={3} onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

interface ManualRankingEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  teams: Team[];
  eventId: number;
  leagueId: number;
  onSave: () => void;
}

const ManualRankingEditorModal: React.FC<ManualRankingEditorModalProps> = ({
                                                                             isOpen,
                                                                             onClose,
                                                                             teams,
                                                                             eventId,
                                                                             leagueId,
                                                                             onSave,
                                                                           }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay/>
      <ModalContent>
        <ModalHeader>Edit Team Rankings</ModalHeader>
        <ModalCloseButton/>
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text fontSize="sm" color="gray.500">
              Drag and drop teams to reorder rankings
            </Text>
            <ManualRankingEditor
              teams={teams}
              eventId={eventId}
              leagueId={leagueId}
              onClose={onClose}
              onSave={onSave}
            />
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
